import { AbilityBuilder, defineAbility } from '@casl/ability';
import { abilitiesPlugin } from '@casl/vue';
import { createPinia, PiniaVuePlugin } from 'pinia';
import VMdDateRangePicker from 'v-md-date-range-picker';
import Vue, { PluginFunction } from 'vue';
import { Fragment } from 'vue-frag';
import VueRouter from 'vue-router';
import Toasted from 'vue-toasted';
import VuejsDialog from 'vuejs-dialog';
import Vuetify from 'vuetify';

import i18n from '#/i18n';
import { observeUserActivity } from '#/lib';
import { routes } from '#/router';

import FormInputElement from './components/form/FormInputElement.vue';
import vuetify from './plugins/vuetify';

type PluginOptions = {
  createRouter: () => VueRouter;
  parentRouteName?: string;
};

const install: PluginFunction<PluginOptions> = (vue: typeof Vue, options?: PluginOptions): void => {
  if (options === undefined) {
    throw new Error('Plugin options can not be undefined');
  }

  vue.use(PiniaVuePlugin);
  const pinia = createPinia();

  const { createRouter, parentRouteName } = options;
  const router = createRouter();

  if (parentRouteName) {
    const parentRoute = router.options.routes?.find((route) => route.name === parentRouteName);
    if (!parentRoute) {
      throw new Error(`Unable to find parent route with name "${parentRouteName}"`);
    }
    parentRoute.children = [
      ...(parentRoute.children || []),
      ...routes.map((route) => {
        if (route.path.startsWith('/')) {
          return { ...route, path: route.path.substring(1) };
        }
        return route;
      }),
    ];
    router.addRoute(parentRoute);
  } else {
    router.addRoutes(routes);
  }

  vue.use(Toasted, { theme: 'custom', position: 'bottom-center', duration: 3000, router });
  vue.use(Vuetify);
  vue.use(VuejsDialog);
  vue.use(VMdDateRangePicker);

  Vue.component('Fragment', Fragment);
  // TODO: Remove if vue-tsc supports recursive component imports
  Vue.component('FormInputElement', FormInputElement);

  observeUserActivity();

  vue.use(
    abilitiesPlugin,
    defineAbility((can: AbilityBuilder['can'], cannot: AbilityBuilder['cannot']) => {
      cannot('manage', 'all'); // TODO: find better default ability
    }),
  );

  Object.assign(options, {
    vuetify,
    i18n,
    pinia,
    router,
  });
};

export default install;
